<template>
  <v-container class="new-device-page" fluid>
    <v-breadcrumbs :items="items" class="px-0 pt-0 pb-2" />
    <div class="new-device-page__header">
      <h1 class="new-device-page__title">Добавление устройства</h1>
      <v-btn text class="text-none" color="primary" to="/device">
        <v-icon left small>mdi-arrow-left</v-icon>
        К списку устройств
      </v-btn>
    </div>

    <v-form ref="form" v-model="valid" lazy-validation>
      <v-card class="new-device-page__card" flat outlined>
        <div class="new-device-page__section new-device-page__section--type">
          <div class="new-device-page__section-title new-device-page__section-title--type">
            <v-icon left small>mdi-format-list-bulleted-type</v-icon>
            Тип устройства
          </div>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                label="Тип устройства"
                :items="typeDevices"
                v-model="typeDevice"
                item-text="name"
                item-value="value"
                @change="handleTypeDeviceChange"
                prepend-inner-icon="mdi-format-list-bulleted-type"
                outlined
                dense
                hide-details
                class="new-device-page__field"
              />
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-0" />

        <div class="new-device-page__section">
          <div class="new-device-page__section-title">
            <v-icon left small>mdi-information-outline</v-icon>
            Основные данные
          </div>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="IP-адрес"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-ip-outline"
                :disabled="typeDevice === 'G'"
                :required="typeDevice !== 'G'"
                v-model="ip_address"
                :rules="typeDevice === 'G' ? [] : validateIpAddress(ip_address)"
              >
                <template v-slot:append>
                  <v-tooltip v-if="ip_address" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click.prevent="autocomplete()">
                        <v-icon small>mdi-format-list-bulleted</v-icon>
                      </v-btn>
                    </template>
                    <span>Автозаполнение</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="MAC-адрес"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-access-point"
                :required="typeDevice !== 'C' && typeDevice !== 'G'"
                v-model="mac_address"
                :rules="typeDevice === 'G' ? [] : validateMacAddress(mac_address)"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                v-if="typeDevice"
                label="Производитель устройства"
                :items="manufacturerItems"
                item-text="name"
                item-value="value"
                prepend-inner-icon="mdi-cogs"
                v-model="manufacturer"
                :rules="[() => !!manufacturer || 'Обязательное поле']"
                required
                outlined
                dense
                hide-details
                return-object
                @change="selectOption()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="Серийный номер"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-counter"
                v-model="serial_number"
                :rules="[() => !!serial_number || 'Обязательное поле']"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="Модель"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-cog-outline"
                v-model="model_name"
                disabled
              />
            </v-col>
            <v-col v-if="roleName !== 'agent'" cols="12" sm="6" md="4">
              <v-autocomplete
                label="Агент"
                :items="organizations"
                item-text="user.name"
                item-value="id"
                prepend-inner-icon="mdi-account-search"
                v-model="agent"
                :rules="[() => !!agent || 'Обязательное поле']"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-textarea
                label="Технический комментарий"
                outlined
                dense
                hide-details
                auto-grow
                rows="2"
                prepend-inner-icon="mdi-tools"
                placeholder="IP, версия прошивки, заметки по настройке"
                v-model="technical_comment"
                :disabled="!isTechnicalCommentEditable"
              />
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-0" />

        <div class="new-device-page__section">
          <div class="new-device-page__section-title">
            <v-icon left small>mdi-phone-in-talk-outline</v-icon>
            SIP и связь
          </div>
          <v-row align="center">
            <v-col cols="12" sm="6" md="4" v-if="typeDevice !== 'C' && typeDevice !== 'G'">
              <v-checkbox
                label="Настройки SIP"
                hide-details
                :disabled="typeDevice !== 'B'"
                v-model="isActiveSipSettings"
                dense
                color="primary"
                class="mt-0"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" class="new-device-page__sip-buttons">
              <v-btn
                v-if="typeDevice === 'G'"
                small
                outlined
                color="primary"
                class="text-none"
                rounded
                @click="getFreeCityPhone()"
              >
                <v-icon left small>mdi-phone</v-icon>
                Получить городской номер
              </v-btn>
              <v-btn
                v-else-if="typeDevice !== 'C'"
                small
                outlined
                color="primary"
                class="text-none"
                rounded
                @click="getFreeSip()"
              >
                <v-icon left small>mdi-account-key-outline</v-icon>
                Получить SIP настройки
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="isActiveSipSettings">
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="SIP имя"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-rename-box-outline"
                v-model="sip.name"
                :rules="[() => !isActiveSipSettings || !!sip.name || 'Обязательное поле']"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="SIP пароль"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-lock-alert-outline"
                v-model="sip.pass"
                :rules="[() => !isActiveSipSettings || !!sip.pass || 'Обязательное поле']"
              />
            </v-col>
            <v-col v-if="typeDevice === 'B'" cols="12" sm="6" md="4">
              <v-text-field
                label="SIP сервер"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-clipboard-alert-outline"
                v-model="sip.address"
              />
            </v-col>
          </v-row>
          <v-row v-if="typeDevice === 'G'">
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="GSM телефон"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-phone"
                v-model="gsm_phone"
                @input="handleGsmPhoneInput"
                :rules="validateGsmPhone"
                :maxlength="16"
                placeholder="+7 (XXX) XXX-XX-XX"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                label="Городской номер (переадресация)"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-phone-in-talk"
                v-model="phone_number"
                disabled
                :rules="[() => !!phone_number || 'Обязательное поле']"
              />
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-0" />

        <div class="new-device-page__actions">
          <v-btn
            color="primary"
            class="text-none"
            rounded
            :disabled="!valid"
            @click.prevent="createDevice"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Сохранить
          </v-btn>
          <v-btn
            color="primary"
            outlined
            class="text-none"
            rounded
            @click.prevent="cancelDevice"
          >
            <v-icon left>mdi-close</v-icon>
            Отменить
          </v-btn>
        </div>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import formatPhoneNumberTextField from '@/mixins/formatPhoneNumberTextField';

export default {
  name: 'DeviceNew',
  props: ['id'],
  mixins: [formatPhoneNumberTextField],
  data () {
    return {
      valid: true,
      resultMacAddress: {},
      updateResult: {},
      ip_address: '',
      serial_number: '',
      mac_address: '',
      manufacturer: {},
      model_name: '',
      agent: '',
      model_id: null,
      isActiveSipSettings: true,
      rotation: 0,
      typeDevice: 'D',
      items: [
        {
          text: 'Устройства',
          disabled: false,
          href: '/device',
        },
        {
          text: 'Новое устройство',
          disabled: true,
          href: 'breadcrumbs_link_1',
        },
      ],
      typeDevices: [
        {
          value: 'D',
          name: 'Домофон',
        },
        {
          value: 'B',
          name: 'ГРЗ',
        },
        {
          value: 'C',
          name: 'Контроллер',
        },
        {
          value: 'G',
          name: 'GSM модуль',
        },
      ],
      deviceManufacturerController: [],
      deviceManufacturersGates: [],
      deviceManufacturers: [],
      deviceManufacturersGsm: [],
      doorphoneModelOptions: [
        { name: 'Rubetek (RV-3434)', modelName: 'RV-3434' },
        { name: 'Rubetek (RV-3438)', modelName: 'RV-3438' },
        { name: 'Beward (DKS-1512)', modelName: 'DKS1512' },
        { name: 'Beward (DKS-15134)', modelName: 'DKS15134' },
        { name: 'Beward (DKS-20211)', modelName: 'DKS20211' },
        { name: 'Akuvox', modelName: 'E12' },
        { name: 'Ufanet (Secret-Top)', modelName: 'Secret-Top' },
        { name: 'ISCom (Sokol-Plus)', modelName: 'Sokol-Plus' },
      ],
      controllerModelOptions: [
        { name: 'Rubetek (RACS-1101)', modelName: 'RACS-1101' },
      ],
      gateModelOptions: [
        { name: 'Beward (DS07P-LP)', modelName: 'DS07P-LP' },
      ],
      sip: {
        name: '',
        pass: '',
        address: '',
      },
      gsm_phone: '',
      city_phone_number_id: null,
      phone_number: '',
      technical_comment: '',
    };
  },
  watch: {
    isActiveSipSettings (newVal) {
      if (!newVal) {
        this.sip.name = '';
        this.sip.pass = '';
        this.sip.address = '';
      }
    },
  },
  computed: {
    ...mapGetters({
      organizations: 'Agents/agents',
      roleName: 'roleName',
      manufacturers: 'DeviceManufacturer/manufacturers',
    }),
    manufacturerItems () {
      if (this.typeDevice === 'D') return this.deviceManufacturers;
      if (this.typeDevice === 'C') return this.deviceManufacturerController;
      if (this.typeDevice === 'B') return this.deviceManufacturersGates;
      if (this.typeDevice === 'G') return this.deviceManufacturersGsm;
      return [];
    },
    validateGsmPhone () {
      const rules = [];
      rules.push(() => !!this.gsm_phone || 'Обязательное поле');
      if (this.gsm_phone) {
        const digitCount = this.gsm_phone.replace(/\D/g, '').length;
        rules.push(() => (digitCount >= 11 && digitCount <= 11) || 'Номер должен содержать 11 цифр (с 7).');
      }
      return rules;
    },
    isTechnicalCommentEditable () {
      return ['admin', 'admin-engineer'].includes(this.roleName);
    },
  },
  methods: {
    ...mapActions({
      getDeviceInfo: 'Device/getDeviceInfo',
      newDevice: 'Device/newDevice',
      getDeviceFreeSip: 'Device/getDeviceFreeSip',
      getDeviceFreeCityPhone: 'Device/getDeviceFreeCityPhone',
      newDeviceBarrier: 'Device/newDeviceBarrier',
      newDeviceController: 'Device/newDeviceController',
      newDeviceGsm: 'Device/newDeviceGsm',
      getDevices: 'Device/getDevices',
      loadAllManufacturer: 'DeviceManufacturer/getManufacturers',
      loadAllAgents: 'Agents/loadAllAgents',
    }),
    handleTypeDeviceChange () {
      this.model_name = '';
      this.gsm_phone = '';
      this.city_phone_number_id = null;
      this.phone_number = '';
      if (this.typeDevice === 'D') {
        this.isActiveSipSettings = true;
      }
      if (this.typeDevice === 'C') {
        this.isActiveSipSettings = false;
      } else if (this.typeDevice === 'G') {
        this.isActiveSipSettings = false;
      } else {
        this.isActiveSipSettings = true;
      }
    },
    getFreeSip () {
      this.getDeviceFreeSip()
        .then((res) => {
          this.sip.name = res.data.sip_name;
          this.sip.pass = res.data.sip_password;
        })
        .catch((err) => {
          if (err.message.includes('422')) {
            const response = err.response.data;
            for (const input in response) {
              response[input].forEach(error => this.$flash_warning(input + ': ' + error));
            }
          } else {
            this.$flash_warning('Не удалось получить sip настройки');
          }
        });
    },
    getFreeCityPhone () {
      this.getDeviceFreeCityPhone()
        .then((res) => {
          this.city_phone_number_id = res.data.id;
          this.phone_number = res.data.phone_number;
        })
        .catch((err) => {
          if (err.message && err.message.includes('422')) {
            const response = err.response.data;
            for (const input in response) {
              response[input].forEach(error => this.$flash_warning(input + ': ' + error));
            }
          } else {
            this.$flash_warning('Не удалось получить свободный городской номер');
          }
        });
    },
    autocomplete () {
      this.getDeviceInfo({
        ipAddress: this.ip_address,
        type: this.typeDevice,
      })
        .then((res) => {
          this.updateResult = res;
          this.assignment();
        })
        .catch((err) => {
          if (err.message.includes('422')) {
            const response = err.response.data;
            for (const input in response) {
              response[input].forEach(error => this.$flash_warning(input + ': ' + error));
            }
          } else {
            this.$flash_warning('Не удалось произвести автозаполнение');
          }
        });
    },
    assignment () {
      this.serial_number = this.updateResult.serial_number;
      this.mac_address = this.updateResult.mac_address;
      this.model_name = this.updateResult.model_name;
      this.model_id = this.updateResult.model_id;

      if (this.updateResult.model_name === 'DS07P-LP') {
        this.typeDevice = 'B';
      }

      const item = this.manufacturerItems.find(m => m.value === this.updateResult.model_id);
      this.manufacturer = item || {
        name: this.updateResult.model_name,
        value: this.updateResult.model_id,
      };
      this.selectOption();
    },
    validateIpAddress () {
      const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const isRequired = this.typeDevice !== 'G';

      return [
        v => {
          const value = (v != null ? String(v) : '').trim();
          if (!value) return isRequired ? 'Обязательное поле' : true;
          return ipRegex.test(value) || 'Укажите корректный ip адрес';
        },
      ];
    },
    validateMacAddress () {
      const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})|([0-9a-fA-F]{4}\.[0-9a-fA-F]{4}\.[0-9a-fA-F]{4})$/;
      const isRequired = this.typeDevice !== 'C' && this.typeDevice !== 'G';

      return [
        v => {
          const value = (v != null ? String(v) : '').trim();
          if (!value) return isRequired ? 'Обязательное поле' : true;
          return macRegex.test(value) || 'Укажите корректный mac адрес';
        },
      ];
    },
    async createDevice () {
      this.$refs.form.validate();
      await this.$nextTick();

      if (this.valid) {
        const dataDevice = {
          ip_address: this.ip_address,
          serial_number: this.serial_number,
          model_id: this.model_id,
        };

        if (this.typeDevice === 'C' || this.typeDevice === 'G') {
          if (this.mac_address) {
            dataDevice.mac_address = this.mac_address;
          }
        } else {
          dataDevice.mac_address = this.mac_address;
        }

        if (this.typeDevice === 'G') {
          dataDevice.gsm_phone = this.normalizeGsmPhone(this.gsm_phone);
          dataDevice.city_phone_number_id = this.city_phone_number_id;
          if (this.ip_address) {
            dataDevice.ip_address = this.ip_address;
          }
        } else {
          dataDevice.ip_address = this.ip_address;
          if (this.isActiveSipSettings) {
            dataDevice.sip_name = this.sip.name;
            dataDevice.sip_password = this.sip.pass;
            if (this.typeDevice === 'B') {
              dataDevice.sip_address = this.sip.address;
            }
          }
        }

        if (this.roleName !== 'agent') {
          dataDevice.agent_id = this.agent;
        }
        if (this.isTechnicalCommentEditable) {
          dataDevice.technical_comment = this.technical_comment ? this.technical_comment : null;
        }

        try {
          await this.createNewDevice(dataDevice);
          this.$flash_success('Устройство успешно создано');
          await this.$router.push({ name: 'Device' });
        } catch (err) {
          this.handleDeviceCreationError(err);
        }
      }
    },

    async createNewDevice (dataDevice) {
      if (this.typeDevice === 'B') {
        await this.newDeviceBarrier(dataDevice);
      } else if (this.typeDevice === 'C') {
        await this.newDeviceController(dataDevice);
      } else if (this.typeDevice === 'G') {
        await this.newDeviceGsm(dataDevice);
      } else {
        dataDevice.type = this.typeDevice;
        await this.newDevice(dataDevice);
      }
    },
    handleDeviceCreationError (err) {
      if (err.message.includes('422')) {
        const response = err.response.data;
        for (const input in response) {
          response[input].forEach(error => this.$flash_warning(input + ': ' + error));
        }
      } else {
        this.$flash_warning('Не удалось создать устройство');
      }
    },
    selectOption () {
      if (!this.manufacturer || !this.manufacturer.value) {
        return;
      }

      let foundModel = null;
      for (const manufacturerItem of this.manufacturers) {
        foundModel = manufacturerItem.models.find(model => model.id === this.manufacturer.value);
        if (foundModel) {
          this.model_name = foundModel.name;
          this.model_id = foundModel.id;
          break;
        }
      }

      if (!foundModel) {
        console.warn('Модель не найдена для manufacturer.value:', this.manufacturer.value);
        this.model_name = '';
        this.model_id = null;
      }
    },
    normalizeGsmPhone (phone) {
      if (!phone) return '';
      let digitsOnly = phone.replace(/\D/g, '');
      if (digitsOnly.startsWith('7')) {
        digitsOnly = digitsOnly.slice(1);
      } else if (digitsOnly.startsWith('8')) {
        digitsOnly = digitsOnly.slice(1);
      }
      return digitsOnly.slice(0, 10);
    },
    handleGsmPhoneInput (value) {
      this.gsm_phone = this.formatPhoneNumberTextField(value);
    },
    cancelDevice () {
      this.$router.push({ name: 'Device' });
    },
    findModelByName (modelName) {
      for (const manufacturerItem of this.manufacturers || []) {
        const model = (manufacturerItem.models || []).find(m => m.name === modelName);
        if (model) {
          return model;
        }
      }
      return null;
    },
    buildManufacturerOptions (options) {
      return options
        .map(({ name, modelName }) => {
          const model = this.findModelByName(modelName);
          if (!model) {
            return null;
          }
          return { name, value: model.id };
        })
        .filter(Boolean);
    },
    initManufacturerOptions () {
      this.deviceManufacturers = this.buildManufacturerOptions(this.doorphoneModelOptions);
      this.deviceManufacturerController = this.buildManufacturerOptions(this.controllerModelOptions);
      this.deviceManufacturersGates = this.buildManufacturerOptions(this.gateModelOptions);
      this.initGsmManufacturers();
    },
    initGsmManufacturers () {
      const gsmManufacturer = this.manufacturers.find(m => m.name === 'GSM Модуль');
      if (gsmManufacturer && gsmManufacturer.models) {
        this.deviceManufacturersGsm = gsmManufacturer.models.map(model => ({
          name: model.name,
          value: model.id,
        }));
      }
    },
  },
  created () {
    // this.loadAllAgents();
    this.loadAllManufacturer({
      include: ['models'],
    })
      .then(() => {
        this.initManufacturerOptions();
      });
  },
};
</script>

<style lang="scss" scoped>
.new-device-page {
  max-width: 90%;
}

.new-device-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.new-device-page__title {
  font-size: 1.5rem;
  font-weight: 400;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.02em;
}

.new-device-page__card {
  border-radius: 12px;
  overflow: hidden;
}

.new-device-page__section {
  padding: 20px 24px;
}

.new-device-page__section--type {
  padding: 20px 24px;
  background: rgba(25, 118, 210, 0.04);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.new-device-page__section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #546e7a;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
}

.new-device-page__section-title--type {
  font-size: 1rem;
  font-weight: 600;
  color: #37474f;
  margin-bottom: 12px;
}

.new-device-page__field ::v-deep .v-input__prepend-inner .v-icon {
  color: rgba(0, 0, 0, 0.5);
}

.new-device-page__sip-buttons {
  display: flex;
  align-items: center;
}

.new-device-page__actions {
  padding: 16px 24px 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

@media screen and (max-width: 600px) {
  .new-device-page__header {
    flex-direction: column;
    align-items: flex-start;
  }
  .new-device-page__title {
    font-size: 1.4rem;
  }
  .new-device-page__sip-buttons {
    justify-content: flex-start;
  }
}
</style>
